import { useEffect, useState } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOffline } from '@/hooks/useOffline';

import { tAuto } from "@/lib/tAuto";
/**
 * OfflineIndicator - Indicador de estado de conexão
 * 
 * NOTA: Botão de refresh manual foi REMOVIDO conforme requisito
 * para experiência de app nativo controlada. 
 * 
 * A sincronização é automática via React Query quando a conexão
 * é restabelecida. Não há atualização manual disponível.
 */
export function OfflineIndicator() {
  const { online, isSlowConnection, connectionInfo } = useOffline();
  const [showSlow, setShowSlow] = useState(false);

  // Show slow connection warning after 3 seconds
  useEffect(() => {
    if (isSlowConnection && online) {
      const timer = setTimeout(() => setShowSlow(true), 3000);
      return () => clearTimeout(timer);
    } else {
      setShowSlow(false);
    }
  }, [isSlowConnection, online]);

  return (
    <AnimatePresence>
      {!online ? (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-[200] bg-destructive text-destructive-foreground"
        >
          <div className="flex items-center px-4 py-3 max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3">
              <WifiOff className="w-5 h-5 shrink-0" />
              <div className="flex flex-col">
                <span className="font-medium text-sm">{tAuto("auto.components_ui_offlineindicator.1", "Sem conexão")}</span>
                <span className="text-xs opacity-90">
                  Modo offline ativo. Dados serão sincronizados automaticamente quando a conexão for restabelecida.
                </span>
              </div>
            </div>
            {/* Botão de refresh removido - sincronização é automática */}
          </div>
        </motion.div>
      ) : showSlow ? (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-[200] bg-amber-500 text-white"
        >
          <div className="flex items-center justify-between px-4 py-2 max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3">
              <Wifi className="w-4 h-4 shrink-0 opacity-75" />
              <span className="text-sm">
                Conexão lenta ({connectionInfo.effectiveType})
              </span>
            </div>
            <button
              onClick={() => setShowSlow(false)}
              className="text-xs underline opacity-75 hover:opacity-100"
            >
              Dispensar
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Badge to show offline/sync status
 */
export function SyncStatus({ pending }: { pending: number }) {
  if (pending === 0) return null;

  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      className="fixed bottom-20 right-4 z-[100] bg-primary text-primary-foreground px-3 py-2 rounded-full shadow-lg flex items-center gap-2"
    >
      <RefreshCw className="w-4 h-4 animate-spin" />
      <span className="text-sm font-medium">{pending} pendente{pending > 1 ? 's' : ''}</span>
    </motion.div>
  );
}
